import React, { useEffect, useState } from "react";
import {
  Briefcase,
  Loader2,
  HardDrive,
  Send,
  Eye,
  Linkedin,
  AtSign,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmtErr = (e, fallback) =>
  e?.response?.data?.detail || e?.message || fallback || "Something went wrong.";

const CHANNELS = [
  { id: "linkedin", label: "LinkedIn", Icon: Linkedin },
  { id: "threads", label: "Threads", Icon: AtSign },
];

const JobsToDrive = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [driveConnected, setDriveConnected] = useState(false);
  const [driveFolder, setDriveFolder] = useState("Brian ClydeFlow");
  const [channelStatus, setChannelStatus] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);

  const load = async () => {
    try {
      const [{ data: cfg }, { data: l }] = await Promise.all([
        api.get("/jobs-drive/config"),
        api.get("/jobs-drive/log"),
      ]);
      setConfig(cfg.config);
      setDriveConnected(cfg.drive_connected);
      setDriveFolder(cfg.drive_folder || "Brian ClydeFlow");
      setChannelStatus(cfg.channel_status || {});
      setLog(l.items || []);
    } catch (e) {
      toast.error(fmtErr(e, "Couldn't load Jobs automation."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/jobs-drive/config", patch);
      setConfig((c) => ({ ...c, ...data }));
      return true;
    } catch (e) {
      toast.error(fmtErr(e, "Couldn't save."));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    const next = !config.enabled;
    const ok = await save({ enabled: next });
    if (ok)
      toast.success(
        next
          ? `Daily Jobs automation ON — runs at ${config.time} UK.`
          : "Daily Jobs automation paused."
      );
  };

  const toggleChannel = async (id) => {
    const set = new Set(config.channels);
    set.has(id) ? set.delete(id) : set.add(id);
    await save({ channels: [...set] });
  };

  const changeTime = async (t) => {
    if (!/^\d{1,2}:\d{2}$/.test(t)) return;
    await save({ time: t.padStart(5, "0") });
  };

  const doPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const { data } = await api.post("/jobs-drive/run-now", { dry_run: true });
      if (data.status === "preview") setPreview(data);
      else toast.error(data.detail || "Preview failed.");
    } catch (e) {
      toast.error(fmtErr(e, "Preview failed."));
    } finally {
      setPreviewing(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/jobs-drive/run-now", { dry_run: false });
      if (data.drive_error) toast.error(`Drive: ${data.drive_error}`);
      else if (data.status === "ok")
        toast.success(`${data.row_count} jobs zipped to Drive & posted! 🚀`);
      else toast.success(`Done (${data.status}). ${data.row_count} jobs found.`);
      load();
    } catch (e) {
      toast.error(fmtErr(e, "Run failed."));
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="jobsdrive-loading">
        <Loader2 className="animate-spin text-cyan-300" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="jobsdrive-panel">
      {/* Header + master switch */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 bg-[#0A192F] border border-white/10 rounded-sm">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300 flex items-center gap-2">
            <Briefcase size={12} /> Daily Jobs → Drive
          </p>
          <h2 className="font-heading text-2xl font-bold text-white mt-1">
            Jobs, zipped &amp; posted for you.
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            Every day at{" "}
            <span className="text-cyan-200 font-mono">{config.time}</span> UK it searches
            Paisley &amp; Renfrewshire jobs, zips the CSV into your Google Drive folder{" "}
            <span className="text-slate-200">&ldquo;{driveFolder}&rdquo;</span>, then posts the
            freshest roles to your channels.
          </p>
        </div>
        <button
          onClick={toggleEnabled}
          disabled={saving}
          data-testid="jobsdrive-toggle"
          className={`shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-sm font-bold text-xs uppercase tracking-widest transition-colors ${
            config.enabled
              ? "bg-[#00F0FF] text-[#050B14] hover:bg-[#33F3FF]"
              : "border border-cyan-400/40 text-cyan-300 hover:border-cyan-400"
          }`}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Briefcase size={14} />}
          {config.enabled ? "Jobs Auto ON" : "Turn on daily jobs"}
        </button>
      </div>

      {!driveConnected && (
        <div
          className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-400/30 rounded-sm text-amber-200 text-sm"
          data-testid="jobsdrive-nodrive"
        >
          <AlertTriangle size={16} /> Connect Google Drive first (Connections tab) — the ZIP goes
          into your own Drive under &ldquo;{driveFolder}&rdquo;.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Time */}
        <div className="p-5 bg-[#0A192F] border border-white/10 rounded-sm">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <HardDrive size={14} className="text-cyan-300" /> Run time{" "}
            <span className="text-slate-500 font-normal">(UK time)</span>
          </h3>
          <input
            type="time"
            value={config.time}
            onChange={(e) => changeTime(e.target.value)}
            disabled={saving}
            data-testid="jobsdrive-time-input"
            className="px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm"
          />
          <p className="text-xs text-slate-500 mt-2">
            Drive folder: <span className="text-slate-300">{driveFolder}</span> (auto-created).
          </p>
        </div>

        {/* Channels */}
        <div className="p-5 bg-[#0A192F] border border-white/10 rounded-sm">
          <h3 className="text-sm font-bold text-white mb-3">Also post roles to</h3>
          <div className="space-y-2">
            {CHANNELS.map(({ id, label, Icon }) => {
              const live = channelStatus[id];
              const on = config.channels.includes(id);
              return (
                <button
                  key={id}
                  onClick={() => live && toggleChannel(id)}
                  disabled={!live || saving}
                  data-testid={`jobsdrive-channel-${id}`}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-sm border text-sm transition-colors ${
                    on && live
                      ? "border-cyan-400/50 bg-cyan-400/10 text-white"
                      : "border-white/10 text-slate-300 hover:border-white/20"
                  } ${!live ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <span className="flex items-center gap-2">
                    <Icon size={16} /> {label}
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-wider">
                    {!live ? "Not connected" : on ? "On" : "Off"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Preview / run now */}
      <div className="p-5 bg-[#0A192F] border border-white/10 rounded-sm">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="text-sm text-slate-400">Test the search, zip &amp; post right now.</div>
          <div className="flex gap-2">
            <button
              onClick={doPreview}
              disabled={previewing}
              data-testid="jobsdrive-preview-btn"
              className="inline-flex items-center gap-2 px-4 py-2 border border-cyan-400/40 text-cyan-300 hover:border-cyan-400 rounded-sm text-xs uppercase tracking-wider"
            >
              {previewing ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}{" "}
              Preview jobs
            </button>
            <button
              onClick={runNow}
              disabled={running || !driveConnected}
              data-testid="jobsdrive-runnow-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm text-xs uppercase tracking-wider hover:bg-[#33F3FF] disabled:opacity-50"
            >
              {running ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Run
              now
            </button>
          </div>
        </div>

        {preview && (
          <div
            className="mt-4 p-4 bg-[#050B14] border border-cyan-400/20 rounded-sm"
            data-testid="jobsdrive-preview"
          >
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-300 mb-2">
              {preview.row_count} jobs found · would zip to {preview.zip_name}
            </p>
            {preview.post_preview && (
              <p className="text-sm text-slate-300 whitespace-pre-wrap mb-3 border-l-2 border-cyan-400/30 pl-3">
                {preview.post_preview}
              </p>
            )}
            <div className="max-h-52 overflow-auto space-y-1">
              {(preview.rows || []).slice(0, 12).map((r, i) => (
                <div key={i} className="text-xs text-slate-400 truncate">
                  <span className="text-slate-200">{r.title || "—"}</span>
                  {r.employer ? ` · ${r.employer}` : ""}
                  {r.location ? ` · ${r.location}` : ""}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Recent runs */}
      <div className="p-5 bg-[#0A192F] border border-white/10 rounded-sm">
        <h3 className="text-sm font-bold text-white mb-3">Recent runs</h3>
        {log.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="jobsdrive-log-empty">
            Nothing yet — turn it on or hit &ldquo;Run now&rdquo;.
          </p>
        ) : (
          <div className="space-y-2" data-testid="jobsdrive-log">
            {log.map((row) => (
              <div
                key={row.log_id}
                className="flex items-start gap-3 p-3 bg-[#050B14] border border-white/5 rounded-sm"
              >
                {row.status === "ok" ? (
                  <CheckCircle2 size={16} className="text-emerald-400 mt-0.5 shrink-0" />
                ) : (
                  <AlertTriangle size={16} className="text-amber-400 mt-0.5 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white truncate">
                    {row.row_count} jobs · {row.zip_name}
                  </p>
                  <p className="text-xs text-slate-500 font-mono">
                    {row.status}
                    {row.drive_error ? ` · Drive error` : ""}
                    {row.channels
                      ? ` · ${Object.keys(row.channels).join(", ") || "no post"}`
                      : ""}
                  </p>
                </div>
                {row.drive_file?.webViewLink && (
                  <a
                    href={row.drive_file.webViewLink}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`jobsdrive-openzip-${row.log_id}`}
                    className="text-cyan-300 hover:text-cyan-100 shrink-0"
                  >
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobsToDrive;
